import { Input, Switch, Textarea } from '@nextui-org/react';
import { CtyButton } from '../button/CtyButton';

export type AddFieldType = 'string' | 'boolean' | 'number' | 'object';

export type AddFieldDraft = {
    keyName: string;
    configId: string;
    fieldType: AddFieldType;
    stringValue: string;
    numberValue: string;
    booleanValue: boolean;
    objectValue: string;
};

interface AddConfigurationFieldFormProps {
    draft: AddFieldDraft;
    isDisabled?: boolean;
    isAdding?: boolean;
    showConfigId?: boolean;
    onDraftChange: (patch: Partial<AddFieldDraft>) => void;
    onCancel: () => void;
    onAdd: () => void;
    t: (value: string) => string;
}

export function AddConfigurationFieldForm({
    draft,
    isDisabled = false,
    isAdding = false,
    showConfigId = false,
    onDraftChange,
    onCancel,
    onAdd,
    t,
}: Readonly<AddConfigurationFieldFormProps>) {
    return (
        <div className="rounded-xl border border-divider bg-content1 p-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {showConfigId ? (
                    <Input
                        label={t('Config ID')}
                        placeholder={t('Enter UUID')}
                        value={draft.configId}
                        onValueChange={(next) => onDraftChange({ configId: next })}
                        size="sm"
                        variant="bordered"
                        isDisabled={isDisabled || isAdding}
                    />
                ) : null}
                <Input
                    label={t('Key')}
                    value={draft.keyName}
                    onValueChange={(next) => onDraftChange({ keyName: next })}
                    size="sm"
                    variant="bordered"
                    isDisabled={isDisabled || isAdding}
                />
                <select
                    aria-label={t('Type')}
                    className={`h-10 rounded-md border border-default-300 bg-content1 px-2 text-sm${showConfigId ? ' md:col-span-2' : ''}`}
                    value={draft.fieldType}
                    onChange={(event) => onDraftChange({ fieldType: event.target.value as AddFieldType })}
                    disabled={isDisabled || isAdding}
                >
                    <option value="string">{t('String')}</option>
                    <option value="boolean">{t('Boolean')}</option>
                    <option value="number">{t('Number')}</option>
                    <option value="object">{t('Object')}</option>
                </select>
            </div>

            <div className="mt-3">
                {draft.fieldType === 'string' && (
                    <Input
                        label={t('Value')}
                        value={draft.stringValue}
                        onValueChange={(next) => onDraftChange({ stringValue: next })}
                        size="sm"
                        variant="bordered"
                        isDisabled={isDisabled || isAdding}
                    />
                )}

                {draft.fieldType === 'boolean' && (
                    <div className="flex items-center gap-3 rounded-lg border border-divider bg-content1 px-3 py-2">
                        <span className="text-sm text-default-700">{t('Value')}</span>
                        <Switch
                            isSelected={Boolean(draft.booleanValue)}
                            onValueChange={(next) => onDraftChange({ booleanValue: next })}
                            isDisabled={isDisabled || isAdding}
                        />
                        <span className="text-sm text-default-500">
                            {draft.booleanValue ? 'true' : 'false'}
                        </span>
                    </div>
                )}

                {draft.fieldType === 'number' && (
                    <Input
                        type="number"
                        label={t('Value')}
                        value={draft.numberValue}
                        onValueChange={(next) => onDraftChange({ numberValue: next })}
                        size="sm"
                        variant="bordered"
                        isDisabled={isDisabled || isAdding}
                    />
                )}

                {draft.fieldType === 'object' && (
                    <Textarea
                        label={t('Value (JSON object)')}
                        minRows={6}
                        value={draft.objectValue}
                        onValueChange={(next) => onDraftChange({ objectValue: next })}
                        variant="bordered"
                        classNames={{ input: 'font-mono text-sm' }}
                        isDisabled={isDisabled || isAdding}
                    />
                )}
            </div>

            <div className="mt-3 flex justify-end gap-2">
                <CtyButton size="sm" variant="bordered" onPress={onCancel} isDisabled={isAdding}>
                    {t('Cancel')}
                </CtyButton>
                <CtyButton size="sm" color="primary" onPress={onAdd} isDisabled={isDisabled} isLoading={isAdding}>
                    {t('Add')}
                </CtyButton>
            </div>
        </div>
    );
}

